import {Component, OnInit} from '@angular/core';
import {CouponService} from '../shared/services/coupon.service';
import {CouponDtoModel} from '../shared/models/coupon.dto.model';
import {ErrorStateMatcher} from '@angular/material/core';
import {UntypedFormBuilder, Validators} from '@angular/forms';
import {LocalizationService} from '../shared/services/localization.service';
import {LoaderService} from '../shared/services/loader.service';
import {ConfirmationService, LazyLoadEvent, MessageService} from 'primeng/api';
import {PaginationArgs} from '../shared/models/pagination.args';

@Component({
    selector: 'njoy-coupon-managment',
    templateUrl: './coupon-managment.component.html',
    styleUrls: ['./coupon-managment.component.scss']
})
export class CouponManagmentComponent implements OnInit {

    public coupons: any[] = [];
    public totalRecords = 0;
    public first = 0;
    public row = 0;
    public paginationArgs: PaginationArgs = {pageNumber: 0, pageSize: 10};

    public addNewCategoryDialog = false;
    public modalTitle = '';

    public couponForm;
    public matcher = new ErrorStateMatcher();

    public duplicatedCodeMessageError = false;

    public couponTypes = [];
    public selectedCouponTypes;
    private selectedUuid: string;
    public codeToSearch = '';

    constructor(private couponService: CouponService,
                private formBuilder: UntypedFormBuilder,
                private loaderService: LoaderService,
                private confirmationService: ConfirmationService,
                private messageService: MessageService,
                private localizationService: LocalizationService) {
    }

    ngOnInit(): void {
        this.findAllCoupons();

        this.couponTypes.push({label: this.localizationService.translate('COUPON.FREE_PERIOD_MONTHS'), value: 'FREE_PERIOD_MONTHS'});
        this.couponTypes.push({label: this.localizationService.translate('COUPON.PERCENTAGE'), value: 'PERCENTAGE'});
        this.couponTypes.push({label: this.localizationService.translate('COUPON.FREE_TRIAL_MONTHS'), value: 'FREE_TRIAL_MONTHS'});

    }

    public onLazyLoad(event: LazyLoadEvent) {
        this.paginationArgs = {pageNumber: event.first / event.rows, pageSize: event.rows};
        this.findAllCoupons();
    }

    private initForm(coupon?: CouponDtoModel) {
        this.couponForm = this.formBuilder.group({
            code: [coupon ? coupon.code : '', [
                Validators.required, Validators.maxLength(50)
            ]],
            discountValue: [coupon ? coupon.discountValue : 0, [
                Validators.required, Validators.min(0)
            ]],
            quantity: [coupon ? coupon.quantity : 1, [
                Validators.required, Validators.min(1)
            ]],
            expiresAt: [coupon ? coupon.expiresAt : '', [
                Validators.required
            ]],
        });
        this.selectedCouponTypes = coupon ? this.couponTypes.find(type => type.value === coupon.discountType) : this.couponTypes[0];
        this.selectedUuid = coupon ? coupon.uuid : undefined;
    }

    public addNewOrUpdateModal(coupon?: CouponDtoModel): void {
        this.duplicatedCodeMessageError = false;
        this.modalTitle = coupon ? this.localizationService.translate('COUPON.UPDATE_MODAL_TITLE') + coupon.code : this.localizationService.translate('COUPON.ADD_NEW');
        this.initForm(coupon);
        this.addNewCategoryDialog = true;
    }

    public addOrUpdateCoupon(): void {
        this.duplicatedCodeMessageError = false;
        this.displayRequiredMessage();
        if (this.couponForm.status === 'VALID') {
            if (this.selectedUuid) {
                const coupon = this.setValues();
                coupon.uuid = this.selectedUuid;
                this.updateCoupon(coupon);
            } else {
                const coupon = this.setValues();
                this.saveNewCoupon(coupon);
            }
        }
    }

    private saveNewCoupon(coupon: CouponDtoModel): void {
        this.loaderService.startLoading();
        this.couponService.createCoupon(coupon).subscribe((data: CouponDtoModel) => {
                this.coupons.push(data);
                this.loaderService.stopLoading();
                this.addNewCategoryDialog = false;
            }, (err: any) => {
                this.loaderService.stopLoading();
                console.log(err);
                if (err.error && err.error.code && err.error.code === 4040) {
                    this.duplicatedCodeMessageError = true;
                }
            }
        );
    }

    private updateCoupon(coupon: CouponDtoModel): void {
        this.loaderService.startLoading();
        this.couponService.updateCoupon(coupon).subscribe((data: CouponDtoModel) => {
                const index = this.coupons.findIndex(value => value.uuid === data.uuid);
                this.coupons.splice(index, 1);
                this.coupons.push(data);
                this.loaderService.stopLoading();
                this.addNewCategoryDialog = false;
            }, (err: any) => {
                this.loaderService.stopLoading();
                console.log(err);
                if (err.error && err.error.code && err.error.code === 4040) {
                    this.duplicatedCodeMessageError = true;
                }
            }
        );
    }

    get getControl() {
        return this.couponForm.controls;
    }

    public changeSelectedCoupon(couponEvent: any) {
        this.selectedCouponTypes = couponEvent.value;
    }

    private findAllCoupons(): void {
        this.loaderService.startLoading();
        this.couponService.findAllCouponsByPage(this.codeToSearch.trim(), this.paginationArgs).subscribe((data: any) => {
            this.loaderService.stopLoading();
            this.coupons = data.content;
            this.coupons.forEach(value => value.quantityToDisplay = (+value.quantity) - (+value.used));
            this.totalRecords = data.totalElements;
            this.row = this.paginationArgs.pageSize;
        }, (err: any) => {
            this.loaderService.stopLoading();
            console.log(err);
        });
    }

    private displayRequiredMessage(): void {
        this.couponForm.touched = true;
        const keys = Object.keys(this.couponForm.controls);
        keys.forEach((element: any) => {
            this.couponForm.controls[element].touched = true;
        });
    }

    private setValues(): CouponDtoModel {
        const coupon = new CouponDtoModel();
        coupon.code = this.couponForm.value.code;
        coupon.discountType = this.selectedCouponTypes.value;
        coupon.discountValue = this.couponForm.value.discountValue;
        coupon.quantity = this.couponForm.value.quantity;
        coupon.expiresAt = new Date(this.couponForm.value.expiresAt);
        coupon.expiresAt.setHours(12);
        return coupon;
    }

    public confirmDeleteCoupon(coupon: CouponDtoModel): void {
        this.confirmationService.confirm({
            message: this.localizationService.translate('COUPON.DELETE_CONFIRMATION_MESSAGE') + coupon.code,
            accept: () => {
                this.deleteCoupon(coupon.uuid);
            }
        });
    }

    private deleteCoupon(uuid: string): void {
        this.couponService.deleteCoupon(uuid).subscribe(
            () => {
                this.loaderService.stopLoading();
                this.displayMessage('success', 'CLIENT.SUCCESS_SUMMARY', 'COUPON.DELETE_SUCCESS_DETAIL');
                const index = this.coupons.findIndex(value => value.uuid === uuid);
                this.coupons.splice(index, 1);
            }, (err: any) => {
                console.log(err);
                this.loaderService.stopLoading();
            }
        );
    }


    private displayMessage(severity: string, summary: string, detail: string): void {
        this.messageService.add({
            severity: severity,
            summary: this.localizationService.translate(summary),
            detail: this.localizationService.translate(detail)
        });
    }
}
